import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../mock';
import { Mail, Lock, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { lang } = useLanguage();
  const t = translations[lang]?.auth || translations.en.auth;
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    axios.get(`${API}/compass/org/resolve`).then((r) => setTenant(r.data.organization)).catch(() => {});
    const p = new URLSearchParams(window.location.search);
    if (p.get('sso_error')) setError(`SSO mislukt: ${p.get('sso_error')}`);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      
      if (response.data.requires_2fa) {
        navigate('/verify-2fa', { state: { email: formData.email } });
      } else {
        login(response.data.access_token, response.data.user);
        navigate('/portal');
      }
    } catch (err) {
      setError(err.response?.data?.detail || t.loginFailed);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            {tenant?.branding?.logo_url
              ? <img src={tenant.branding.logo_url} alt={tenant.name} style={{ maxHeight: 56, marginBottom: 8 }} />
              : <h1 className="auth-logo" style={tenant?.branding?.primary_color ? { color: tenant.branding.primary_color } : undefined}>{tenant?.branding?.login_title || 'KRYPTON9'}</h1>}
            <h2 className="heading-2">{tenant ? (tenant.branding?.login_title || tenant.name) : t.welcomeBack}</h2>
            <p className="body-medium text-secondary">{tenant?.branding?.login_subtitle || t.signInSubtitle}</p>
          </div>

          {tenant?.sso_enabled && (
            <button type="button" className="btn-primary auth-btn" style={tenant?.branding?.primary_color ? { background: tenant.branding.primary_color } : undefined}
              onClick={() => { window.location.href = `${API}/compass/org/sso/${tenant.slug}/login`; }}>
              {lang === 'nl' ? `Inloggen met ${tenant.name} SSO` : `Sign in with ${tenant.name} SSO`}
            </button>
          )}

          {error && <div className="auth-error">{error}</div>}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label className="body-small">{t.emailLabel}</label>
              <div className="input-with-icon">
                <Mail size={18} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder={t.emailPlaceholder}
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="body-small">{t.passwordLabel}</label>
              <div className="input-with-icon">
                <Lock size={18} />
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <button type="submit" disabled={loading} className="btn-primary auth-btn" data-testid="login-submit-btn">
              {loading ? t.signingIn : t.signIn}
              <ArrowRight size={16} />
            </button>
          </form>

          <div className="auth-footer">
            <p className="body-small">
              {t.noAccount}{' '}
              <Link to="/register" className="auth-link">{t.createAccount}</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
